import {datadogRum} from '@datadog/browser-rum';
import * as Sentry from '@sentry/browser';
import {rewriteFramesIntegration} from '@sentry/integrations';
import type {StackFrame, TransactionEvent} from '@sentry/types';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {pageLoadID} from 'ClientHeaders';
import * as errorconfig from 'ErrorConfig';

import * as Asserts from '../Asserts';
import * as Logging from '../util/Logging';

const skipFunctions = new Set([
  ...Object.keys(Asserts),
  ...Object.entries(Logging)
    .filter((kv) => 'function' === typeof kv[1])
    .map((kv) => kv[0]),
  'log', // log is not exported by Logging.ts
]);

export function rewriteFrames(frame: StackFrame): StackFrame {
  if (frame.filename) {
    const hostnameRegex = new RegExp(window.location.hostname, 'i');
    frame.in_app =
      (hostnameRegex.test(frame.filename) || /bnchcdn\.com/i.test(frame.filename)) &&
      // Skip Logging.ts and Asserts.ts because they're unhelpful in the sentry fingerprint
      (!frame.function || !skipFunctions.has(frame.function));
  }
  return frame;
}

export function beforeSendTransaction(transaction: TransactionEvent): TransactionEvent | null {
  const minimumDurationMs = transaction.transaction
    ? BenchlingConfig.PERF_ENABLE_JS_SELF_SAMPLING_PROFILER?.perfEventMinimumDurationMs[
        transaction.transaction
      ]
    : null;
  if (
    transaction.timestamp &&
    transaction.start_timestamp &&
    minimumDurationMs &&
    (transaction.timestamp - transaction.start_timestamp) * 1000 < minimumDurationMs
  ) {
    return null;
  }
  return transaction;
}

export function addRumContextToEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const rumContext = datadogRum.getInternalContext();
  if (event.tags == null) {
    event.tags = {};
  }
  event.tags.rum_session_id = rumContext?.session_id;

  return event;
}

export function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null {
  event = addRumContextToEvent(event);
  return errorconfig.dataCallback(event, hint);
}

export function configureSentry(sentryDSN: string): void {
  Sentry.init({
    dsn: sentryDSN,
    maxBreadcrumbs: 50,
    environment: window.DEPLOY_ENVIRONMENT,
    initialScope: {
      tags: {
        build_timestamp: window.BUILD_TIMESTAMP?.toISOString(),
        client_api_version: window.clientAPIVersion,
        page_load_id: pageLoadID,
        browser_supported: window.isBrowserSupported,
        has_flash: benchlingUtil.hasFlash,
        // "site" is deprecated in favor of "deploy" but some integrations still expect the former
        site: window.DEPLOY_NAME,
        deploy: window.DEPLOY_NAME,
        tenant: window.TENANT_SUBDOMAIN,
      },
    },
    allowUrls: errorconfig.USER_FACING_WHITE_LISTED_URLS,
    release: window.REVISION,
    ignoreErrors: errorconfig.IGNORED_ERRORS,
    beforeSend,
    beforeSendTransaction,
    integrations: [
      Sentry.browserTracingIntegration({
        instrumentNavigation: false,
        instrumentPageLoad: false,
      }),
      Sentry.browserProfilingIntegration(),
      // We define our own window.onunhandledrejection that has our own custom logic, so disable Raven's.
      Sentry.globalHandlersIntegration({onunhandledrejection: false}),
      // sentry integration changed typing on IntegrationFnResult to include undefined, which we don't
      // return anyway, but this causes a typing error.
      // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rewriteFramesIntegration({
        iteratee(frame: StackFrame) {
          return rewriteFrames(frame);
        },
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    tracesSampleRate: BenchlingConfig.PERF_ENABLE_JS_SELF_SAMPLING_PROFILER?.tracesSampleRate,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [window.TENANT_SUBDOMAIN],
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // result in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: BenchlingConfig.PERF_ENABLE_JS_SELF_SAMPLING_PROFILER?.profilesSampleRate,
  });
}
